import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Select, message, Space, Upload, Image } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import GalleryService from '../../../services/GalleryService';

const { Option } = Select;

const Gallery = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await GalleryService.getGalleries();
      setList(res.data || []);
    } catch (err) {
      console.error(err);
      message.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setFileList([]);
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      language: record.language,
    });
    setFileList(record.image ? [{ uid: '-1', name: 'existing-image', status: 'done', url: record.image }] : []);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete gallery item',
      content: 'Are you sure?',
      async onOk() {
        try {
          await GalleryService.deleteGallery(id);
          message.success('Deleted');
          fetch();
        } catch (err) {
          console.error(err);
          message.error('Delete failed');
        }
      }
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const beforeUpload = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      message.error('Invalid image type. Allowed: jpg, png, webp');
      return Upload.LIST_IGNORE;
    }
    if (file.size > maxSize) {
      message.error('Image must be smaller than 5MB');
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const onUploadChange = ({ fileList: newList }) => {
    setFileList(newList);
  };

  const onFinish = async (values) => {
    const payload = {
      language: values.language || null,
    };

    const uploadFiles = fileList
      .map((file) => file.originFileObj)
      .filter(Boolean);

    if (!editing && uploadFiles.length === 0) {
      message.error('Upload at least one image');
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        const newFile = uploadFiles[0] || null;
        await GalleryService.updateGallery(editing.id, payload, newFile);
        message.success('Updated');
      } else {
        await Promise.all(uploadFiles.map((file) => GalleryService.createGallery(payload, file)));
        message.success(uploadFiles.length > 1 ? `Uploaded ${uploadFiles.length} photos` : 'Created');
      }

      setIsModalOpen(false);
      form.resetFields();
      setFileList([]);
      fetch();
    } catch (err) {
      console.error(err);
      message.error('Save failed');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Image', dataIndex: 'image', key: 'image', render: (v) => v ? <Image src={v} width={80} /> : null },
    { title: 'Language', dataIndex: 'language', key: 'language' },
    { title: 'Actions', key: 'actions', render: (_, r) => (
      <Space>
        <Button icon={<EyeOutlined />} size="small" onClick={()=>handleView(r)}>View</Button>
        <Button icon={<EditOutlined />} size="small" onClick={()=>openEdit(r)}>Edit</Button>
        <Button danger icon={<DeleteOutlined />} size="small" onClick={()=>handleDelete(r.id)}>Delete</Button>
      </Space>
    ) }
  ];

  return (
    <div style={{padding:16}}>
      <div style={{display:'flex', justifyContent:'space-between', marginBottom:12, gap:12}}>
        <h3 style={{margin:0}}>Photo Gallery</h3>
        <div style={{flexShrink:0}}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add</Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={list}
        rowKey="id"
        loading={loading}
        pagination={{pageSize:10}}
      />

      <Modal title={editing ? 'Edit Gallery Item' : 'Create Gallery Item'} open={isModalOpen} onCancel={()=>{setIsModalOpen(false); form.resetFields(); setFileList([]);}} footer={null}>
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item label="Images" required={!editing}>
            <Upload
              accept="image/*"
              multiple
              listType="picture-card"
              beforeUpload={beforeUpload}
              fileList={fileList}
              onChange={onUploadChange}
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Select</div>
              </div>
            </Upload>
            {!editing && fileList.length === 0 && <div style={{color:'red', marginTop:4}}>Upload at least one image</div>}
          </Form.Item>

          <Form.Item name="language" label="Language" initialValue="Marathi">
            <Select allowClear>
              <Option value="English">English</Option>
              <Option value="Marathi">Marathi</Option>
              <Option value="Hindi">Hindi</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>{editing ? 'Update' : 'Create'}</Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Gallery Item" open={isViewOpen} onCancel={()=>setIsViewOpen(false)} footer={[<Button key="close" onClick={()=>setIsViewOpen(false)}>Close</Button>]}> 
        {viewing && (
          <div>
            {viewing.image && <p><strong>Image:</strong><br/><img src={viewing.image} alt="gallery" style={{maxWidth:300}}/></p>}
            <p><strong>Language:</strong> {viewing.language}</p>
            {viewing.created_at && <p><strong>Created:</strong> {new Date(viewing.created_at).toLocaleString()}</p>}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Gallery;
